import * as React from "react"
import { EditorContent, useEditor } from "@tiptap/react"
import { StarterKit } from "@tiptap/starter-kit"
import { Image } from "@tiptap/extension-image"
import { TaskItem } from "@tiptap/extension-task-item"
import { TaskList } from "@tiptap/extension-task-list"
import { TextAlign } from "@tiptap/extension-text-align"
import { Typography } from "@tiptap/extension-typography"
import { Highlight } from "@tiptap/extension-highlight"
import { Subscript } from "@tiptap/extension-subscript"
import { Superscript } from "@tiptap/extension-superscript"
import { Underline } from "@tiptap/extension-underline"
import { MiniToolbar } from "./MiniToolbar"

interface RichTextEditorProps {
  value: string | object; // Accepts HTML or JSON
  onChange: (value: object) => void; // Save as JSON
  onBlur?: (e: React.FocusEvent) => void;
  placeholder?: string;
  editor?: any; // Optional external editor instance
  noteId?: string | number; // Add noteId to detect note switch
  editorReady?: (editor: any) => void; // Callback for when editor is ready
  className?: string; // Override wrapper class
  editable?: boolean; // false = read-only (e.g. shared note with VIEW permission)
  /**
   * Live co-editing session. The consuming app supplies pre-configured
   * Collaboration/caret extensions (TipTap major differs per app, so the
   * shared editor can't import them itself). `key` identifies the session —
   * the editor is recreated when it changes. Yjs owns the content in this mode.
   */
  collab?: {
    extensions: any[];
    key?: unknown;
  } | null;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Start writing...",
  editor: externalEditor,
  noteId,
  editorReady,
  className,
  editable = true,
  collab,
}) => {
  const internalEditor = useEditor({
    extensions: [
      // Collaboration supplies its own shared history stack — disable the
      // built-in one in live mode (option is `history` in v2, `undoRedo` in v3).
      collab ? StarterKit.configure({ history: false, undoRedo: false } as any) : StarterKit,
      Image,
      TaskList,
      TaskItem,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Typography,
      Highlight,
      Subscript,
      Superscript,
      Underline,
      ...(collab ? collab.extensions : []),
    ],
    // Yjs hydrates content in live mode — never set it locally.
    content: collab ? undefined : value,
    editorProps: {
      attributes: {
        placeholder,
      },
    },
    editable,
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON()); // Always save as JSON
    },
  }, [collab?.key]);

  const editor = externalEditor || internalEditor;

  // Keep the TipTap instance in sync when the editable flag flips
  // (e.g. switching between an owned note and a view-only shared note).
  React.useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  // Custom copy handler to clean up excessive whitespace
  React.useEffect(() => {
    if (editor) {
      const handleCopy = (event: ClipboardEvent) => {
        const selection = editor.state.selection;
        if (selection.empty) return;

        // Get the selected content as plain text
        const selectedText = editor.state.doc.textBetween(
          selection.from,
          selection.to,
          '\n'
        );

        // Clean up excessive whitespace while preserving document structure
        const cleanedText = selectedText
          .replace(/\n\s*\n\s*\n\s*\n+/g, '\n\n') // Replace 4+ consecutive newlines with 2
          .replace(/^\s+|\s+$/g, '') // Trim start and end
          .replace(/\n\s{2,}/g, '\n ') // Replace multiple spaces after newline with single space
          .replace(/\s{2,}\n/g, ' \n'); // Replace multiple spaces before newline with single space

        // Set the cleaned text to clipboard
        event.clipboardData?.setData('text/plain', cleanedText);
        event.preventDefault();
      };

      // Add copy event listener
      editor.view.dom.addEventListener('copy', handleCopy);

      return () => {
        editor.view.dom.removeEventListener('copy', handleCopy);
      };
    }
  }, [editor]);

  // Only update content when noteId changes (i.e., switching notes).
  // Skipped entirely in live mode — the Yjs document is the source of truth.
  const lastNoteId = React.useRef<string | number | undefined>();
  React.useEffect(() => {
    if (collab) return;
    if (editor && noteId !== lastNoteId.current) {
      if (typeof value === "object" && value !== null) {
        editor.commands.setContent(value, false);
      } else if (typeof value === "string") {
        editor.commands.setContent(value, false);
      }
      lastNoteId.current = noteId;
    }
  }, [noteId, value, editor, collab]);

  // Call editorReady when editor is available
  React.useEffect(() => {
    if (editor && editorReady) {
      editorReady(editor);
    }
  }, [editor, editorReady]);

  if (!editor) return <div>Loading editor...</div>;
  return (
    <div className={className ?? "flex flex-col h-full rounded-xl border border-hintword-orange/50 bg-background/60 p-2 transition-all duration-200 shadow-sm"}>
      {editable && <MiniToolbar editor={editor} />}
      <div className="flex-1 overflow-y-auto editor-scroll-area">
        <EditorContent editor={editor} onBlur={onBlur} />
      </div>
    </div>
  );
} 